package features.timeoff.handlers

import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.html.*
import kotlinx.html.dom.append
import shared.ui.ModalManager
import AppState
import Router
import features.timeoff.data.TimeOffRepository
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLSelectElement
import org.w3c.dom.HTMLTextAreaElement

// Time Off Modal Functions
fun showTimeOffModal() {
    val teamId = AppState.currentTeam?.id ?: return
    
    // Load categories from Firebase first
    features.timeoff.data.LeaveCategoryRepository.getLeaveCategories(teamId)
        .then { categories ->
            AppState.leaveCategories[teamId] = categories.toMutableList()
            
            // Populate dropdown
            val select = document.getElementById("timeoff-category-input") as? HTMLSelectElement
            if (select != null) {
                select.innerHTML = ""
                categories.forEach { category ->
                    val option = document.createElement("option") as org.w3c.dom.HTMLOptionElement
                    option.value = category.name
                    option.text = category.name
                    select.appendChild(option)
                }
            }
            
            // Reset form fields
            (document.getElementById("timeoff-start-input") as? HTMLInputElement)?.value = ""
            (document.getElementById("timeoff-end-input") as? HTMLInputElement)?.value = ""
            (document.getElementById("timeoff-reason-input") as? HTMLTextAreaElement)?.value = ""
            
            // Reset days display
            val daysDisplay = document.getElementById("timeoff-days-display")
            daysDisplay?.innerHTML = "<span class='text-slate-400 text-sm'>Select dates to calculate days</span>"
            
            ModalManager.show("request-timeoff-modal")
        }
        .catch { error ->
            console.error("❌ Failed to load categories:", error)
            kotlinx.browser.window.alert("Failed to load leave categories. Please try again.")
        }
}

fun submitTimeOffRequest() {
    console.log("📝 Submitting time off request...")
    
    val teamId = AppState.currentTeam?.id
    val userId = AppState.currentUser?.id
    
    if (teamId == null || userId == null) {
        kotlinx.browser.window.alert("User or team not found")
        return
    }
    
    // Get form values
    val category = (document.getElementById("timeoff-category-input") as? HTMLSelectElement)?.value
    val startDate = (document.getElementById("timeoff-start-input") as? HTMLInputElement)?.value
    val endDate = (document.getElementById("timeoff-end-input") as? HTMLInputElement)?.value
    val reason = (document.getElementById("timeoff-reason-input") as? HTMLTextAreaElement)?.value
    
    // Validation
    if (category.isNullOrBlank()) {
        kotlinx.browser.window.alert("Please select a leave type")
        return
    }
    
    if (startDate.isNullOrBlank() || endDate.isNullOrBlank()) {
        kotlinx.browser.window.alert("Please select start and end dates")
        return
    }
    
    if (reason.isNullOrBlank()) {
        kotlinx.browser.window.alert("Please provide a reason")
        return
    }
    
    // Validate date range
    val days = TimeOffRepository.calculateDays(startDate, endDate)
    if (days <= 0) {
        kotlinx.browser.window.alert("End date must be after or equal to start date")
        return
    }
    
    // Submit to Firebase
    TimeOffRepository.createTimeOffRequest(
        teamId = teamId,
        userId = userId,
        startDate = startDate,
        endDate = endDate,
        type = category,
        reason = reason
    ).then { requestId ->
        console.log("✅ Time off request created: $requestId")
        kotlinx.browser.window.alert("Time off request submitted successfully!")
        ModalManager.close()
        Router.render() // Refresh to show new request
    }.catch { error ->
        console.error("❌ Failed to create time off request:", error)
        kotlinx.browser.window.alert("Failed to submit request. Please try again.")
    }
}

fun showManageCategories() {
    val teamId = AppState.currentTeam?.id ?: return
    
    // Load both categories and types from Firebase
    val categoriesPromise = features.timeoff.data.LeaveCategoryRepository.getLeaveCategories(teamId)
    val typesPromise = features.timeoff.data.LeaveCategoryRepository.getLeaveTypes(teamId)
    
    categoriesPromise.then { categories ->
        AppState.leaveCategories[teamId] = categories.toMutableList()
        
        typesPromise.then { types ->
            AppState.leaveTypes[teamId] = types.toMutableList()
            renderCategoriesModal()
            ModalManager.show("manage-categories-modal")
        }.catch { error ->
            console.error("❌ Failed to load types:", error)
            renderCategoriesModal()
            ModalManager.show("manage-categories-modal")
        }
    }.catch { error ->
        console.error("❌ Failed to load categories:", error)
        
        // Still try to load types
        typesPromise.then { types ->
            AppState.leaveTypes[teamId] = types.toMutableList()
            renderCategoriesModal()
            ModalManager.show("manage-categories-modal")
        }.catch { typeError ->
            console.error("❌ Failed to load types:", typeError)
            renderCategoriesModal()
            ModalManager.show("manage-categories-modal")
        }
    }
}

fun renderCategoriesModal() {
    val teamId = AppState.currentTeam?.id ?: return
    val categories = AppState.leaveCategories[teamId] ?: emptyList()
    val types = AppState.leaveTypes[teamId] ?: emptyList()
    
    // Render categories list
    val categoriesContainer = document.getElementById("categories-list") as? org.w3c.dom.HTMLElement
    categoriesContainer?.innerHTML = ""
    categoriesContainer?.append {
        if (categories.isEmpty()) {
            div("text-center py-8 text-slate-400") {
                +"No categories yet"
            }
        } else {
            categories.forEach { category ->
                val leaveType = types.find { it.id == category.typeId }
                div("flex items-center justify-between p-3 bg-dark-700 rounded-lg mb-2") {
                    div {
                        span("text-white font-medium") { +category.name }
                        if (leaveType != null) {
                            br {}
                            span("text-slate-400 text-sm") { +leaveType.name }
                        }
                    }
                    button(classes = "px-3 py-1 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 transition text-sm") {
                        attributes["onclick"] = "deleteLeaveCategory('${category.id}')"
                        +"🗑️ Delete"
                    }
                }
            }
        }
    }
    
    // Populate leave type dropdown
    val typeSelect = document.getElementById("category-type-select") as? HTMLSelectElement
    typeSelect?.innerHTML = ""
    types.forEach { type ->
        val option = document.createElement("option") as org.w3c.dom.HTMLOptionElement
        option.value = type.id
        option.text = type.name
        typeSelect?.appendChild(option)
    }
}

fun addLeaveCategory() {
    val teamId = AppState.currentTeam?.id ?: return
    
    val typeSelect = document.getElementById("category-type-select") as? HTMLSelectElement
    val nameInput = document.getElementById("category-name-input") as? HTMLInputElement
    
    val typeId = typeSelect?.value
    val name = nameInput?.value
    
    if (typeId.isNullOrBlank() || name.isNullOrBlank()) {
        kotlinx.browser.window.alert("Please fill in all fields")
        return
    }
    
    features.timeoff.data.LeaveCategoryRepository.addLeaveCategory(teamId, name, typeId)
        .then { categoryId ->
            console.log("✅ Category added: $categoryId")
            nameInput?.value = ""
            
            // Reload categories
            features.timeoff.data.LeaveCategoryRepository.getLeaveCategories(teamId)
                .then { categories ->
                    AppState.leaveCategories[teamId] = categories.toMutableList()
                    renderCategoriesModal()
                }
        }
        .catch { error ->
            console.error("❌ Failed to add category:", error)
            kotlinx.browser.window.alert("Failed to add category")
        }
}

fun deleteLeaveCategory(categoryId: String) {
    val teamId = AppState.currentTeam?.id ?: return
    
    if (!kotlinx.browser.window.confirm("Delete this category?")) {
        return
    }
    
    features.timeoff.data.LeaveCategoryRepository.deleteLeaveCategory(teamId, categoryId)
        .then {
            console.log("✅ Category deleted")
            
            // Reload categories
            features.timeoff.data.LeaveCategoryRepository.getLeaveCategories(teamId)
                .then { categories ->
                    AppState.leaveCategories[teamId] = categories.toMutableList()
                    renderCategoriesModal()
                }
        }
        .catch { error ->
            console.error("❌ Failed to delete category:", error)
            kotlinx.browser.window.alert("Failed to delete category")
        }
}

fun createLeaveType() {
    val teamId = AppState.currentTeam?.id ?: return
    
    val nameInput = document.getElementById("leave-type-name-input") as? HTMLInputElement
    val quotaInput = document.getElementById("leave-type-quota-input") as? HTMLInputElement
    
    val name = nameInput?.value
    val quota = quotaInput?.value?.toIntOrNull() ?: 0
    
    if (name.isNullOrBlank()) {
        kotlinx.browser.window.alert("Please enter a type name")
        return
    }
    
    // isDefault can be false for custom types
    val isDefault = false
    
    features.timeoff.data.LeaveCategoryRepository.createLeaveType(teamId, name, quota, isDefault)
        .then { typeId ->
            console.log("✅ Leave type created: $typeId")
            nameInput?.value = ""
            quotaInput?.value = "0"
            
            // Reload types
            features.timeoff.data.LeaveCategoryRepository.getLeaveTypes(teamId)
                .then { types ->
                    AppState.leaveTypes[teamId] = types.toMutableList()
                    renderCategoriesModal()
                }
        }
        .catch { error ->
            console.error("❌ Failed to create leave type:", error)
            kotlinx.browser.window.alert("Failed to create leave type")
        }
}

fun renderCategoriesList() {
    val teamId = AppState.currentTeam?.id ?: return
    val categories = AppState.leaveCategories[teamId] ?: emptyList()
    val container = document.getElementById("categories-list") as? org.w3c.dom.HTMLElement ?: return
    
    container.innerHTML = ""
    container.append {
        if (categories.isEmpty()) {
            div("text-center py-8 text-slate-400") {
                +"No categories yet"
            }
        } else {
            categories.forEach { category ->
                div("flex items-center justify-between p-3 bg-dark-700 rounded-lg mb-2") {
                    span("text-white") { +category.name }
                    button(classes = "px-3 py-1 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 transition text-sm") {
                        +"Delete"
                    }
                }
            }
        }
    }
}

/**
 * Export time off requests to CSV file
 * Includes all requests for the current team with member names, dates, and status
 */
fun exportTimeOffToCSV() {
    val team = AppState.currentTeam
    val teamId = team?.id
    
    if (team == null || teamId == null) {
        kotlinx.browser.window.alert("No team selected")
        return
    }
    
    console.log("📊 Exporting time off data for team: ${team.name}")
    
    // Fetch time off requests from Firebase
    TimeOffRepository.getTimeOffRequests(teamId)
        .then { requests ->
            console.log("✅ Loaded ${requests.size} time off requests for export")
            
            // ========== DEBUG: Show all fetched data ==========
            console.log("========================================")
            console.log("📊 TIME OFF DATA DEBUG")
            console.log("========================================")
            console.log("Team ID: $teamId")
            console.log("Team Name: ${team.name}")
            console.log("Total Requests: ${requests.size}")
            console.log("----------------------------------------")
            
            // Get members for name lookup
            val members = AppState.members[teamId] ?: emptyList()
            console.log("Total Members in AppState: ${members.size}")
            members.forEach { member ->
                console.log("  - Member: ${member.name} (ID: ${member.id})")
            }
            console.log("----------------------------------------")
            
            // Log each request in detail
            requests.forEachIndexed { index, request ->
                val member = members.find { it.id == request.userId }
                val memberName = member?.name ?: "Unknown (ID: ${request.userId})"
                
                console.log("Request #${index + 1}:")
                console.log("  Member: $memberName")
                console.log("  Category: ${request.type}")
                console.log("  Start Date: ${request.startDate}")
                console.log("  End Date: ${request.endDate}")
                console.log("  Days: ${request.days}")
                console.log("  Status: ${request.status}")
                console.log("  Reason: ${request.reason}")
                console.log("  Submitted: ${request.createdAt}")
                console.log("  ---")
            }
            console.log("========================================")
            // ========== END DEBUG ==========
            
            // Check if there's data to export
            if (requests.isEmpty()) {
                kotlinx.browser.window.alert("No time off data to export")
                return@then
            }
            
            // Build CSV content
            val csvLines = mutableListOf<String>()
            
            // Add header row
            csvLines.add("Member Name,Leave Category,Start Date,End Date,Days,Status,Reason,Submitted Date")
            
            // Add data rows
            requests.forEach { request ->
                val member = members.find { it.id == request.userId }
                val memberName = member?.name ?: "Unknown"
                
                // Format dates
                val startDate = formatDateForCSV(request.startDate)
                val endDate = formatDateForCSV(request.endDate)
                val submittedDate = formatTimestampForCSV(request.createdAt)
                
                // Escape CSV fields
                val escapedMemberName = escapeCSVField(memberName)
                val escapedCategory = escapeCSVField(request.type)
                val escapedReason = escapeCSVField(request.reason)
                val escapedStatus = escapeCSVField(request.status)
                
                // Build row
                val row = "$escapedMemberName,$escapedCategory,$startDate,$endDate,${request.days},$escapedStatus,$escapedReason,$submittedDate"
                csvLines.add(row)
            }
            
            // Join all lines
            val csvContent = csvLines.joinToString("\n")
            
            // Create blob and trigger download
            val blob = org.w3c.files.Blob(arrayOf(csvContent), org.w3c.files.BlobPropertyBag("text/csv;charset=utf-8;"))
            
            // Generate filename with team name and current date
            val now = kotlin.js.Date()
            val year = now.getFullYear().toInt()
            val month = (now.getMonth() + 1).toString().padStart(2, '0')
            val day = now.getDate().toString().padStart(2, '0')
            val dateStr = "$year-$month-$day"
            
            // Sanitize team name for filename
            val sanitizedTeamName = team.name.replace(Regex("[^a-zA-Z0-9-_]"), "_")
            val filename = "timeoff_export_${sanitizedTeamName}_$dateStr.csv"
            
            // Create download link
            val url = window.asDynamic().URL.createObjectURL(blob) as String
            val link = document.createElement("a") as org.w3c.dom.HTMLAnchorElement
            link.href = url
            link.download = filename
            link.style.display = "none"
            
            // Trigger download
            document.body?.appendChild(link)
            link.click()
            document.body?.removeChild(link)
            
            // Clean up URL
            window.asDynamic().URL.revokeObjectURL(url)
            
            console.log("✅ CSV export completed: $filename")
            kotlinx.browser.window.alert("Time off data exported successfully!")
        }
        .catch { error ->
            console.error("❌ Failed to export time off data:", error)
            kotlinx.browser.window.alert("Failed to export data. Please try again.")
        }
}

/**
 * Format date string for CSV (YYYY-MM-DD)
 */
private fun formatDateForCSV(dateStr: String): String {
    return try {
        // Date is already in YYYY-MM-DD format from Firestore
        dateStr
    } catch (e: Exception) {
        console.error("Error formatting date:", e)
        dateStr
    }
}

/**
 * Format timestamp for CSV (YYYY-MM-DD HH:mm)
 */
private fun formatTimestampForCSV(timestamp: String): String {
    return try {
        val date = kotlin.js.Date(timestamp)
        val year = date.getFullYear().toInt()
        val month = (date.getMonth() + 1).toString().padStart(2, '0')
        val day = date.getDate().toString().padStart(2, '0')
        val hours = date.getHours().toString().padStart(2, '0')
        val minutes = date.getMinutes().toString().padStart(2, '0')
        
        "$year-$month-$day $hours:$minutes"
    } catch (e: Exception) {
        console.error("Error formatting timestamp:", e)
        timestamp
    }
}

/**
 * Escape CSV field (handle commas, quotes, newlines)
 */
private fun escapeCSVField(field: String): String {
    // If field contains comma, quote, or newline, wrap in quotes and escape internal quotes
    return if (field.contains(",") || field.contains("\"") || field.contains("\n")) {
        "\"${field.replace("\"", "\"\"")}\""
    } else {
        field
    }
}
